import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, Loader, AlertCircle, CheckCircle, Play, ChevronDown, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { adminApi } from '../../api/admin';

const POLL_MS = 3000;

const AdminMondiali = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [mode, setMode] = useState('club');
  const [worldCupStartDate, setWorldCupStartDate] = useState('2026-06-10');
  const [worldCupEndDate, setWorldCupEndDate] = useState('2026-07-20');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [convocated, setConvocated] = useState(null);
  const [convocatedLoading, setConvocatedLoading] = useState(false);
  const [convocatedRefreshing, setConvocatedRefreshing] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [d10sOnlyFilter, setD10sOnlyFilter] = useState(false);

  const pollRef = useRef(null);
  const convocatedPollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const stopConvocatedPolling = () => {
    if (convocatedPollRef.current) {
      clearInterval(convocatedPollRef.current);
      convocatedPollRef.current = null;
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await adminApi.getMondialiNextMatchSync();
      setMode(res?.mode === 'national' ? 'national' : 'club');
      setWorldCupStartDate(res?.worldCupStartDate || '2026-06-10');
      setWorldCupEndDate(res?.worldCupEndDate || '2026-07-20');
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore caricamento impostazioni',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConvocatedCache = useCallback(async () => {
    try {
      setConvocatedLoading(true);
      const res = await adminApi.getMondialiConvocated();
      if (res?.success && res?.teams?.length > 0) {
        setConvocated(res);
        return true;
      }
      if (res?.status === 'empty') {
        setConvocated(null);
      }
      return false;
    } catch (err) {
      setConvocated(null);
      return false;
    } finally {
      setConvocatedLoading(false);
    }
  }, []);

  const pollConvocatedRefresh = useCallback(() => {
    stopConvocatedPolling();
    convocatedPollRef.current = setInterval(async () => {
      try {
        const st = await adminApi.getMondialiConvocatedStatus();
        if (st?.status === 'running') return;
        stopConvocatedPolling();
        setConvocatedRefreshing(false);
        if (st?.result?.error) {
          setMessage({ type: 'error', text: st.result.error });
          return;
        }
        if (st?.result?.success) {
          setConvocated(st.result);
          setMessage({
            type: 'success',
            text: `Rose aggiornate: ${st.result.total_convocated ?? 0} convocati, ${st.result.total_d10s_convocated ?? 0} con carta D10S`,
          });
        }
      } catch (err) {
        stopConvocatedPolling();
        setConvocatedRefreshing(false);
        setMessage({
          type: 'error',
          text: err?.response?.data?.error || err?.message || 'Errore polling rose',
        });
      }
    }, POLL_MS);
  }, []);

  const startConvocatedRefresh = useCallback(async () => {
    if (convocatedRefreshing) return;
    try {
      setConvocatedRefreshing(true);
      setMessage({ type: '', text: '' });
      const res = await adminApi.refreshMondialiConvocated();
      if (res?.started === false) {
        setMessage({ type: 'error', text: res?.message || 'Refresh già in corso' });
        pollConvocatedRefresh();
        return;
      }
      setMessage({
        type: 'success',
        text: 'Caricamento rose da SportMonks (~2 min). Puoi lasciare la pagina aperta…',
      });
      pollConvocatedRefresh();
    } catch (err) {
      setConvocatedRefreshing(false);
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore avvio refresh rose',
      });
    }
  }, [convocatedRefreshing, pollConvocatedRefresh]);

  useEffect(() => {
    loadSettings();
    return () => {
      stopPolling();
      stopConvocatedPolling();
    };
  }, [loadSettings]);

  useEffect(() => {
    if (mode === 'national' && !loading) {
      (async () => {
        const hasCache = await loadConvocatedCache();
        if (!hasCache) {
          startConvocatedRefresh();
        }
      })();
    }
  }, [mode, loading, loadConvocatedCache, startConvocatedRefresh]);

  const pollSyncStatus = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const st = await adminApi.getMondialiNextMatchSyncRunStatus();
        if (st?.status === 'running') return;
        stopPolling();
        setSyncRunning(false);
        setSyncResult(st?.result || null);
        if (st?.result?.error) {
          setMessage({ type: 'error', text: st.result.error });
        } else if (st?.result) {
          const r = st.result;
          setMessage({
            type: 'success',
            text: `Sync completata: convocati=${r.called_up ?? 0}, NON CONVOCATO=${r.not_called_up ?? 0}, API SportMonks=${r.sportmonks_api_calls ?? 0}`,
          });
          if (mode === 'national') {
            startConvocatedRefresh();
          }
        }
      } catch (err) {
        stopPolling();
        setSyncRunning(false);
        setMessage({
          type: 'error',
          text: err?.response?.data?.error || err?.message || 'Errore polling sync',
        });
      }
    }, POLL_MS);
  }, [mode, startConvocatedRefresh]);

  const handleRunSync = async () => {
    if (syncRunning) return;
    try {
      setSyncRunning(true);
      setSyncResult(null);
      setMessage({ type: '', text: '' });
      const res = await adminApi.runMondialiNextMatchSync();
      if (res?.started === false) {
        setMessage({ type: 'error', text: res?.message || 'Sync già in corso' });
        pollSyncStatus();
        return;
      }
      setMessage({
        type: 'success',
        text: 'Sync Mondiale avviata. Rosa WC per nazionale, poi assegnazione giocatori…',
      });
      pollSyncStatus();
    } catch (err) {
      setSyncRunning(false);
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore avvio sync',
      });
    }
  };

  const handleToggle = async (nextMode) => {
    if (nextMode === mode || saving) return;
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const res = await adminApi.setMondialiNextMatchSyncMode(nextMode);
      setMode(res?.mode === 'national' ? 'national' : 'club');
      setWorldCupStartDate(res?.worldCupStartDate || worldCupStartDate);
      setWorldCupEndDate(res?.worldCupEndDate || worldCupEndDate);
      setMessage({
        type: 'success',
        text:
          nextMode === 'national'
            ? 'Scheduler userà sync nazionali. Usa «Esegui sync Mondiale ora» per aggiornare subito le carte.'
            : 'Scheduler userà sync squadre club.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Errore salvataggio',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const isNational = mode === 'national';
  const teams = convocated?.teams || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Globe className="w-8 h-8 mr-3 text-yellow-400" />
          Mondiali
        </h1>
      </div>

      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-emerald-900/40 border border-emerald-600/50 text-emerald-200'
              : 'bg-red-900/40 border border-red-600/50 text-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 max-w-2xl space-y-6 mb-8">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Sync prossima partita (app)</h2>
          <p className="text-sm text-gray-400 mb-6">
            Una modalità attiva nello scheduler. La sync Mondiale carica prima le rose WC per nazionale,
            poi in rosa → partita WC, fuori rosa → NON CONVOCATO (senza API per giocatore).
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader className="w-5 h-5 animate-spin" />
              Caricamento...
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button
                  type="button"
                  disabled={saving || syncRunning}
                  onClick={() => handleToggle('club')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold border transition-colors ${
                    !isNational
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-gray-900/60 border-gray-600 text-gray-300 hover:border-gray-500'
                  } ${saving || syncRunning ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  Squadre club
                </button>
                <button
                  type="button"
                  disabled={saving || syncRunning}
                  onClick={() => handleToggle('national')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold border transition-colors ${
                    isNational
                      ? 'bg-yellow-600 border-yellow-500 text-white'
                      : 'bg-gray-900/60 border-gray-600 text-gray-300 hover:border-gray-500'
                  } ${saving || syncRunning ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  Nazionali (Mondiale)
                </button>
              </div>

              <div className="rounded-lg bg-gray-900/70 border border-gray-700 p-4 text-sm text-gray-300 space-y-2 mb-6">
                <p>
                  <span className="text-gray-500">Scheduler:</span>{' '}
                  <span className="text-white font-medium">
                    {isNational ? 'Nazionali — World Cup' : 'Squadre club'}
                  </span>
                </p>
                {isNational && (
                  <>
                    <p>
                      1. Rose WC per ogni nazionale del torneo (SportMonks)
                      <br />
                      2. Match WC per nazionale
                      <br />
                      3. Giocatore in rosa → partita; fuori rosa → <span className="text-amber-400">NON CONVOCATO</span>
                    </p>
                    <p>
                      Calendario WC:{' '}
                      <span className="text-yellow-300 font-medium">
                        {worldCupStartDate} → {worldCupEndDate}
                      </span>
                    </p>
                  </>
                )}
              </div>

              {isNational && (
                <button
                  type="button"
                  disabled={syncRunning}
                  onClick={handleRunSync}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold ${
                    syncRunning
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}
                >
                  {syncRunning ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {syncRunning ? 'Sync Mondiale in corso…' : 'Esegui sync Mondiale ora'}
                </button>
              )}

              {syncResult && !syncRunning && (
                <pre className="mt-4 p-3 rounded bg-gray-950 text-xs text-gray-400 overflow-x-auto max-h-48">
                  {JSON.stringify(syncResult, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      </div>

      {isNational && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-yellow-400" />
                Convocati per nazionale
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Rose SportMonks (48 nazioni). Carta D10S = basic, plus, rare, epic o unique emessa.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={d10sOnlyFilter}
                  onChange={(e) => setD10sOnlyFilter(e.target.checked)}
                  className="rounded border-gray-600"
                />
                Solo con carta D10S
              </label>
              <button
                type="button"
                disabled={convocatedLoading || convocatedRefreshing}
                onClick={startConvocatedRefresh}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
              >
                {convocatedRefreshing ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {convocatedRefreshing ? 'Caricamento rose…' : 'Aggiorna rose'}
              </button>
            </div>
          </div>

          {(convocatedLoading || convocatedRefreshing) && !convocated?.teams?.length && (
            <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
              <Loader className="w-6 h-6 animate-spin" />
              {convocatedRefreshing
                ? 'Caricamento rose da SportMonks in background (~2 min)…'
                : 'Lettura cache…'}
            </div>
          )}

          {convocated && !convocatedLoading && (
            <p className="text-sm text-gray-400 mb-4">
              <span className="text-white font-medium">{convocated.total_convocated}</span> convocati su{' '}
              <span className="text-white font-medium">{convocated.total_teams}</span> nazionali —{' '}
              <span className="text-amber-300 font-medium">{convocated.total_d10s_convocated}</span> con carta D10S
              {convocated.cached && (
                <span className="text-gray-500"> (cache {convocated.cache_age_sec ?? 0}s)</span>
              )}
            </p>
          )}

          {convocated && teams.length > 0 && (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {teams.map((team) => {
                const players = d10sOnlyFilter
                  ? (team.players || []).filter((p) => p.has_d10s_card)
                  : team.players || [];
                if (d10sOnlyFilter && players.length === 0) return null;
                const isOpen = expandedTeams[team.team_id];
                const displayCount = d10sOnlyFilter ? players.length : team.player_count;
                return (
                  <div
                    key={team.team_id}
                    className="rounded-lg border border-gray-700 bg-gray-900/50 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleTeam(team.team_id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-800/80 transition-colors"
                    >
                      <span className="flex items-center gap-2 font-medium text-white">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                        {team.team_name}
                      </span>
                      <span className="text-sm text-gray-400 shrink-0 ml-2">
                        {displayCount} convocati
                        {team.d10s_count > 0 && (
                          <span className="ml-2 text-amber-400">· {team.d10s_count} D10S</span>
                        )}
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="px-4 pb-3 pt-0 border-t border-gray-700/80 divide-y divide-gray-800">
                        {players.map((p) => (
                          <li
                            key={p.sportmonks_id}
                            className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm"
                          >
                            <span className="text-gray-200">{p.name}</span>
                            <span className="flex items-center gap-2 text-xs text-gray-500">
                              <span>SM {p.sportmonks_id}</span>
                              {p.has_d10s_card && (
                                <span className="px-2 py-0.5 rounded bg-amber-900/50 text-amber-300 border border-amber-700/50">
                                  D10S{p.d10s_name ? `: ${p.d10s_name}` : ''}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {convocated && teams.length === 0 && !convocatedLoading && (
            <p className="text-gray-500 text-sm">Nessuna rosa caricata.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMondiali;
